import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import DataTable from 'react-data-table-component';

import { informacionSucursal, limpiarSucursal } from '../../actions/sucursales';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { formatNumber } from '../../utils/generalFunctions';

const tiposSucursal = [
    { value: 0, label: 'Fija' },
    { value: 1, label: 'Móvil' }
];

const tiposEmpleado = [
    { value: 0, label: 'Cajero' },
    { value: 1, label: 'Bateador' },
    { value: 2, label: 'Promotor' }, 
    { value: 3, label: 'Freidor' }, 
    { value: 4, label: 'Vendedor ambulante' }, 
    { value: 5, label: 'Administrador' }
];

const formatFecha = (fecha) => fecha
    ? new Date(fecha).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '-';

const formatFechaHora = (fecha) => fecha
    ? new Date(fecha).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

const formatNombre = (persona) => persona
    ? `${persona.nombre || ''} ${persona.apellido_paterno || ''} ${persona.apellido_materno || ''}`.trim()
    : '-';

const DetallesSucursal = () => {

    const dispatch = useDispatch();
    const { id } = useParams();

    const informacion = useSelector(state => state.sucursales.informacion);

    const [asistenciaDetalle, setAsistenciaDetalle] = useState(null);

    useEffect(() => {
        if (id) {
            dispatch(informacionSucursal(id));
            dispatch(editarBreadcrumb([
                { nombre: 'Sucursales', path: '/sucursales', activo: false },
                { nombre: 'Detalles de la sucursal', path: `/sucursales/detalles/${id}`, activo: true }
            ]));
        }

        return () => {
            dispatch(limpiarSucursal());
        };
    }, [id]);

    const tipoSucursal = tiposSucursal.find(t => t.value === informacion?.sucursal?.tipo);

    /* Columnas */
    const columnsEmpleados = [
        {
            name: 'Nombre',
            sortable: true,
            selector: row => row.nombre || '-',
            grow: 2
        },
        {
            name: 'Apellido paterno',
            sortable: true,
            selector: row => row.apellido_paterno || '-',
            grow: 1.5
        },
        {
            name: 'Apellido materno',
            sortable: true,
            selector: row => row.apellido_materno || '-',
            grow: 1.5
        },
        {
            name: 'Tipo',
            sortable: true,
            selector: row => {
                const tipo = tiposEmpleado.find(t => t.value === row.tipo);
                return tipo ? tipo.label : '-';
            }
        }
    ];

    const columnsAsistencias = [
        {
            name: 'Fecha',
            sortable: true,
            selector: row => formatFechaHora(row.fecha)
        },
        {
            name: 'Responsable',
            sortable: true,
            selector: row => formatNombre(row.responsable),
            grow: 2
        },
        {
            name: 'Total empleados',
            sortable: true,
            selector: row => row.total_empleados ?? '-'
        },
        {
            name: 'Total asistencias',
            sortable: true,
            selector: row => row.total_asistencias ?? '-'
        },
        {
            name: '',
            cell: row => (
                <button
                    className="btn btn-sm btn-info"
                    title="Ver detalle"
                    onClick={() => setAsistenciaDetalle(row)}
                >
                    <i className="fas fa-eye"></i>
                </button>
            )
        }
    ];

    const columnsVentas = [
        {
            name: 'Folio',
            sortable: true,
            selector: row => row.folio || '-'
        },
        {
            name: 'Vendedor',
            sortable: true,
            selector: row => formatNombre(row.vendedor),
            grow: 1.5
        },
        {
            name: 'Método',
            sortable: true,
            selector: row => row.metodo?.nombre || '-'
        },
        {
            name: 'Total',
            sortable: true,
            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-'
        },
        {
            name: 'Fecha',
            sortable: true,
            selector: row => formatFechaHora(row.fecha)
        },
        {
            name: 'Status',
            sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Realizada' : 'Cancelada'}
                </span>
            )
        }
    ];

    const columnsVueltas = [
        {
            name: 'Folio',
            sortable: true,
            selector: row => row.folio || '-'
        },
        {
            name: 'Vendedor',
            sortable: true,
            selector: row => formatNombre(row.vendedor),
            grow: 1.5
        },
        {
            name: 'Total',
            sortable: true,
            selector: row => row.total != null ? formatNumber(row.total, 2, '$', '') : '-'
        },
        {
            name: 'Fecha',
            sortable: true,
            selector: row => formatFechaHora(row.fecha)
        },
        {
            name: 'Status',
            sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-danger'}`}>
                    {row.status === true ? 'Realizada' : 'Cancelada'}
                </span>
            )
        }
    ];

    const columnsCortes = [
        {
            name: 'Folio',
            sortable: true,
            selector: row => row.folio || '-'
        },
        {
            name: 'Responsable',
            sortable: true,
            selector: row => formatNombre(row.responsable),
            grow: 1.5
        },
        {
            name: 'Fecha',
            sortable: true,
            selector: row => formatFechaHora(row.fecha)
        },
        {
            name: 'Total inicial',
            sortable: true,
            selector: row => row.total_inicial != null ? formatNumber(row.total_inicial, 2, '$', '') : '-'
        },
        {
            name: 'Total ventas',
            sortable: true,
            selector: row => row.total_ventas != null ? formatNumber(row.total_ventas, 2, '$', '') : '-'
        },
        {
            name: 'Total ventas amb.',
            sortable: true,
            selector: row => row.total_vueltas != null ? formatNumber(row.total_vueltas, 2, '$', '') : '-'
        },
        {
            name: 'Total gastos',
            sortable: true,
            selector: row => row.total_gastos != null ? formatNumber(row.total_gastos, 2, '$', '') : '-'
        },
        {
            name: 'Status',
            sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Finalizado' : 'Pendiente'}
                </span>
            )
        }
    ];

    const columnsRevisiones = [
        {
            name: 'Responsable',
            sortable: true,
            selector: row => formatNombre(row.responsable),
            grow: 1.5
        },
        {
            name: 'Confirmador',
            sortable: true,
            selector: row => formatNombre(row.confirmador),
            grow: 1.5
        },
        {
            name: 'Fecha creación',
            sortable: true,
            selector: row => formatFechaHora(row.fecha_creacion)
        },
        {
            name: 'Fecha revisión',
            sortable: true,
            selector: row => row.fecha_revision ? formatFechaHora(row.fecha_revision) : '-'
        },
        {
            name: 'Status',
            sortable: true,
            cell: row => (
                <span className={`badge ${row.status === true ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.status === true ? 'Revisada' : 'Pendiente'}
                </span>
            )
        },
        {
            name: 'Total puntos',
            sortable: true,
            selector: row => row.total_puntos ?? '-'
        },
        {
            name: 'Puntos bien',
            sortable: true,
            selector: row => row.puntos_bien ?? '-'
        }
    ];

    const noDataComponent = (icono, texto) => (
        <div className="py-5 text-center text-muted">
            <i className={`fas ${icono} fa-2x mb-2 d-block opacity-25`}></i>
            {texto}
        </div>
    );

    return (
        <>
        <div className="row mt-5">

            {/* Header */}
            <div className="col-12 mb-4">
                <div className="d-flex align-items-center gap-3">
                    <div className="rounded-circle bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, minWidth: 48 }}>
                        <i className="fas fa-store text-primary fs-5"></i>
                    </div>
                    <div>
                        <h4 className="mb-0 fw-semibold">{informacion?.sucursal?.nombre || 'Detalles de la sucursal'}</h4>
                        <small className="text-muted">Información completa de la sucursal</small>
                    </div>
                    <div className="ms-auto">
                        {tipoSucursal && (
                            <span className="badge fs-6 px-3 py-2 bg-success">
                                <i className={`fas ${informacion?.sucursal?.tipo === 1 ? 'fa-truck' : 'fa-building'} me-1`}></i>
                                {tipoSucursal.label}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Acordeones */}
            <div className="col-12">
                <div className="accordion" id="accordionDetallesSucursal">

                    {/* Sección 1: Información general */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingInfo">
                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInfo" aria-expanded="true" aria-controls="collapseInfo">
                                <i className="fas fa-info-circle text-primary me-2"></i>
                                Información general
                            </button>
                        </h2>
                        <div id="collapseInfo" className="accordion-collapse collapse show" aria-labelledby="headingInfo" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body">
                                <div className="row g-3">
                                    <div className="col-md-2">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Nombre</div>
                                            <div className="fw-semibold">{informacion?.sucursal?.nombre || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border-start border-primary border-3 ps-3">
                                            <div className="text-muted small mb-1">Evento</div>
                                            <div className="fw-semibold">{informacion?.sucursal?.evento?.nombre || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="border-start border-info border-3 ps-3">
                                            <div className="text-muted small mb-1">Tipo</div>
                                            <div className="fw-semibold">{tipoSucursal?.label || '-'}</div>
                                        </div>
                                    </div>
                                    <div className="col-md-5">
                                        <div className="border-start border-secondary border-3 ps-3">
                                            <div className="text-muted small mb-1">Dirección</div>
                                            <div className="fw-semibold">{informacion?.sucursal?.direccion || '-'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sección 2: Empleados */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingEmpleados">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseEmpleados" aria-expanded="false" aria-controls="collapseEmpleados">
                                <i className="fas fa-users text-warning me-2"></i>
                                Empleados asignados
                                {informacion?.empleados?.length > 0 && (
                                    <span className="badge bg-warning rounded-pill ms-2">{informacion.empleados.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="collapseEmpleados" className="accordion-collapse collapse" aria-labelledby="headingEmpleados" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsEmpleados}
                                    data={informacion?.empleados || []}
                                    pagination
                                    paginationPerPage={10}
                                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                    highlightOnHover
                                    noDataComponent={noDataComponent('fa-users', 'No hay empleados asignados')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 3: Asistencias */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingAsistencias">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseAsistencias" aria-expanded="false" aria-controls="collapseAsistencias">
                                <i className="fas fa-clipboard-check text-info me-2"></i>
                                Historial de asistencias
                                {informacion?.asistencias?.length > 0 && (
                                    <span className="badge bg-info rounded-pill ms-2">{informacion.asistencias.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="collapseAsistencias" className="accordion-collapse collapse" aria-labelledby="headingAsistencias" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsAsistencias}
                                    data={informacion?.asistencias || []}
                                    pagination
                                    paginationPerPage={10}
                                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                    highlightOnHover
                                    noDataComponent={noDataComponent('fa-clipboard-check', 'No hay asistencias registradas')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 4: Ventas */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingVentas">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseVentas" aria-expanded="false" aria-controls="collapseVentas">
                                <i className="fas fa-shopping-cart text-success me-2"></i>
                                Historial de ventas
                                {informacion?.ventas?.length > 0 && (
                                    <span className="badge bg-success rounded-pill ms-2">{informacion.ventas.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="collapseVentas" className="accordion-collapse collapse" aria-labelledby="headingVentas" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsVentas}
                                    data={informacion?.ventas || []}
                                    pagination
                                    paginationPerPage={10}
                                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                    highlightOnHover
                                    noDataComponent={noDataComponent('fa-shopping-cart', 'No hay ventas registradas')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 5: Vueltas */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingVueltas">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseVueltas" aria-expanded="false" aria-controls="collapseVueltas">
                                <i className="fas fa-redo text-success me-2"></i>
                                Historial de ventas ambulantes
                                {informacion?.vueltas?.length > 0 && (
                                    <span className="badge bg-success rounded-pill ms-2">{informacion.vueltas.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="collapseVueltas" className="accordion-collapse collapse" aria-labelledby="headingVueltas" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsVueltas}
                                    data={informacion?.vueltas || []}
                                    pagination
                                    paginationPerPage={10}
                                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                    highlightOnHover
                                    noDataComponent={noDataComponent('fa-redo', 'No hay ventas ambulantes registradas')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 6: Cortes de caja */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingCortes">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseCortes" aria-expanded="false" aria-controls="collapseCortes">
                                <i className="fas fa-cash-register text-danger me-2"></i>
                                Historial de cortes de caja
                                {informacion?.cortes?.length > 0 && (
                                    <span className="badge bg-danger rounded-pill ms-2">{informacion.cortes.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="collapseCortes" className="accordion-collapse collapse" aria-labelledby="headingCortes" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsCortes}
                                    data={informacion?.cortes || []}
                                    pagination
                                    paginationPerPage={10}
                                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                    highlightOnHover
                                    noDataComponent={noDataComponent('fa-cash-register', 'No hay cortes de caja registrados')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 7: Revisiones */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingRevisiones">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseRevisiones" aria-expanded="false" aria-controls="collapseRevisiones">
                                <i className="fas fa-tasks text-warning me-2"></i>
                                Historial de revisiones
                                {informacion?.revisiones?.length > 0 && (
                                    <span className="badge bg-warning text-dark rounded-pill ms-2">{informacion.revisiones.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="collapseRevisiones" className="accordion-collapse collapse" aria-labelledby="headingRevisiones" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body p-0">
                                <DataTable
                                    columns={columnsRevisiones}
                                    data={informacion?.revisiones || []}
                                    pagination
                                    paginationPerPage={10}
                                    paginationComponentOptions={{ rowsPerPageText: 'Filas por página', rangeSeparatorText: 'de' }}
                                    highlightOnHover
                                    noDataComponent={noDataComponent('fa-tasks', 'No hay revisiones registradas')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Sección 8: Puntos de revisión */}
                    <div className="accordion-item border-0 shadow-sm mb-2">
                        <h2 className="accordion-header" id="headingPuntos">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapsePuntos" aria-expanded="false" aria-controls="collapsePuntos">
                                <i className="fas fa-map-marker-alt text-secondary me-2"></i>
                                Puntos de revisión
                                {informacion?.sucursal?.puntos?.length > 0 && (
                                    <span className="badge bg-secondary text-white rounded-pill ms-2">{informacion.sucursal.puntos.length}</span>
                                )}
                            </button>
                        </h2>
                        <div id="collapsePuntos" className="accordion-collapse collapse" aria-labelledby="headingPuntos" data-bs-parent="#accordionDetallesSucursal">
                            <div className="accordion-body">
                                {informacion?.sucursal?.puntos?.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2">
                                        {informacion.sucursal.puntos.map((punto, index) => (
                                            <span key={index} className="badge bg-light text-dark border fs-6 px-3 py-2">
                                                <i className="fas fa-dot-circle text-warning me-1"></i>
                                                {punto.nombre || '-'}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted mb-0">No hay puntos de revisión registrados.</p>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

        </div>

        {/* Modal detalle de asistencia */}
        <Modal show={asistenciaDetalle != null} size="lg" backdrop="static" centered onHide={() => setAsistenciaDetalle(null)}>
            <Modal.Header closeButton>
                <Modal.Title>Detalle de asistencia</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {asistenciaDetalle && (
                    <>
                        <div className="row g-3 mb-4">
                            <div className="col-md-4">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Fecha</div>
                                    <div className="fw-semibold">{formatFechaHora(asistenciaDetalle.fecha)}</div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="border-start border-primary border-3 ps-3">
                                    <div className="text-muted small mb-1">Responsable</div>
                                    <div className="fw-semibold">{formatNombre(asistenciaDetalle.responsable)}</div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="border-start border-secondary border-3 ps-3">
                                    <div className="text-muted small mb-1">Empleados</div>
                                    <div className="fw-semibold">{asistenciaDetalle.total_empleados ?? '-'}</div>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="border-start border-success border-3 ps-3">
                                    <div className="text-muted small mb-1">Asistencias</div>
                                    <div className="fw-semibold">{asistenciaDetalle.total_asistencias ?? '-'}</div>
                                </div>
                            </div>
                        </div>

                        <DataTable
                            columns={[
                                {
                                    name: 'Empleado',
                                    sortable: true,
                                    grow: 2,
                                    selector: row => formatNombre(row.empleado)
                                },
                                {
                                    name: 'Tipo',
                                    sortable: true,
                                    selector: row => tiposEmpleado.find(t => t.value === row.empleado?.tipo)?.label || '-'
                                },
                                {
                                    name: 'Asistencia',
                                    sortable: true,
                                    cell: row => (
                                        <span className={`badge ${row.asistio ? 'bg-success' : 'bg-danger'}`}>
                                            {row.asistio ? 'Asistió' : 'No asistió'}
                                        </span>
                                    )
                                }
                            ]}
                            data={asistenciaDetalle.empleados || []}
                            highlightOnHover
                            noDataComponent="Sin empleados registrados"
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-secondary" onClick={() => setAsistenciaDetalle(null)}>
                    Cerrar
                </button>
            </Modal.Footer>
        </Modal>

        </>
    );
};

export default DetallesSucursal;
